import React, { useState } from 'react';
import {
  Bookmark,
  ExternalLink,
  Heart,
  MessageCircle,
  Send,
  Share2,
  ThumbsUp,
} from 'lucide-react';

/* Approval card previews: the thing J.O. is approving, drawn the way it
 * will actually be seen. The card's other fields are Milli's notes about
 * the action; this block is the action itself.
 *
 *   social  → a Facebook post and an Instagram post, real image, caption
 *             with its line breaks, the K.O. CO+RE Systems Page and the
 *             kocoresystems account (the only accounts the publisher posts to)
 *   email   → the message as it lands in an inbox
 *   article → the blog post, rendered from its markdown
 *   content → a batch content item that is not a blog post (plain text)
 *
 * The mocks use fixed light colors on purpose: they imitate the platform,
 * not the dashboard theme. */

const BRAND = 'https://kocoresystems.com/brand';
const PAGE_NAME = 'K.O. CO+RE Systems';
const IG_HANDLE = 'kocoresystems';
const AVATAR = `${BRAND}/ko_core_shield_profile_v1.png`;
const FROM_EMAIL = 'milli@kocoresystems.com';

function Avatar({ size = 40 }) {
  return (
    <img
      src={AVATAR}
      alt=""
      width={size}
      height={size}
      className="rounded-full object-cover bg-[#1e2023] shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

function ChannelTabs({ channels, active, onChange, labels }) {
  if (channels.length < 2) return null;
  return (
    <div className="flex gap-1 mb-2" role="tablist">
      {channels.map((c) => (
        <button
          key={c}
          type="button"
          role="tab"
          aria-selected={active === c}
          onClick={() => onChange(c)}
          className={`px-3 py-1 rounded-full text-xs border transition-colors ${
            active === c
              ? 'bg-foreground text-background border-foreground'
              : 'bg-transparent text-muted-foreground border-border hover:text-foreground'
          }`}
          data-testid={`preview-tab-${c}`}
        >
          {labels[c] || c}
        </button>
      ))}
    </div>
  );
}

function FacebookMock({ caption, imageUrl, t }) {
  return (
    <div
      className="rounded-lg bg-white text-[#050505] border border-[#ced0d4] shadow-sm overflow-hidden max-w-[520px]"
      style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      data-testid="preview-facebook"
    >
      <div className="flex items-center gap-2.5 px-3 pt-3 pb-2">
        <Avatar size={40} />
        <div className="leading-tight">
          <div className="text-[15px] font-semibold">{PAGE_NAME}</div>
          <div className="text-[13px] text-[#65676b]">
            {t('approvals.preview.justNow')} · {t('approvals.preview.public')}
          </div>
        </div>
      </div>
      <div className="px-3 pb-3 text-[15px] leading-[1.35] whitespace-pre-wrap break-words">
        {caption}
      </div>
      {imageUrl ? (
        <img src={imageUrl} alt="" className="block w-full bg-[#f0f2f5]" />
      ) : null}
      <div className="flex justify-around border-t border-[#ced0d4] text-[#65676b] text-[14px] font-medium">
        <span className="flex items-center gap-1.5 py-2">
          <ThumbsUp size={16} /> {t('approvals.preview.like')}
        </span>
        <span className="flex items-center gap-1.5 py-2">
          <MessageCircle size={16} /> {t('approvals.preview.comment')}
        </span>
        <span className="flex items-center gap-1.5 py-2">
          <Share2 size={16} /> {t('approvals.preview.share')}
        </span>
      </div>
    </div>
  );
}

function InstagramMock({ caption, imageUrl, t }) {
  return (
    <div
      className="rounded-lg bg-white text-[#262626] border border-[#dbdbdb] overflow-hidden max-w-[470px]"
      style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      data-testid="preview-instagram"
    >
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <Avatar size={32} />
        <div className="text-[14px] font-semibold">{IG_HANDLE}</div>
      </div>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="block w-full aspect-square object-cover bg-black"
        />
      ) : (
        <div className="w-full aspect-square bg-[#efefef] flex items-center justify-center text-[13px] text-[#8e8e8e]">
          {t('approvals.preview.noImage')}
        </div>
      )}
      <div className="flex items-center gap-4 px-3 pt-2.5">
        <Heart size={24} />
        <MessageCircle size={24} />
        <Send size={24} />
        <Bookmark size={24} className="ml-auto" />
      </div>
      <div className="px-3 pt-2 pb-3 text-[14px] leading-[1.35] whitespace-pre-wrap break-words">
        <span className="font-semibold mr-1.5">{IG_HANDLE}</span>
        {caption}
      </div>
    </div>
  );
}

function EmailMock({ to, subject, body, business, support, t }) {
  // FormQuick (095/096) speaks for itself: support replies go out from
  // support@formquick.io, outreach from hello@formquick.io.
  const fq = business === 'FormQuick.io';
  const senderName = fq ? (support ? 'FormQuick Support' : 'J.O. at FormQuick') : `Milli, ${PAGE_NAME}`;
  const senderEmail = fq ? (support ? 'support@formquick.io' : 'hello@formquick.io') : FROM_EMAIL;
  return (
    <div
      className="rounded-lg bg-white text-[#202124] border border-[#dadce0] overflow-hidden max-w-[640px]"
      style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      data-testid="preview-email"
    >
      {support && (
        <div className="px-4 py-3 bg-[#f6f3fb] border-b border-[#e4dcf2] text-[13px]" data-testid="preview-support-context">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#7B4FC0] font-semibold">
            {support.fromName || 'A FormQuick customer'} wrote{support.class ? ` (${support.class.replace('_', ' ')})` : ''}
          </div>
          {support.subject && <div className="mt-1 font-semibold">{support.subject}</div>}
          {support.body && <div className="mt-1 whitespace-pre-wrap break-words text-[#444] max-h-40 overflow-hidden">{support.body}</div>}
        </div>
      )}
      <div className="px-4 pt-3 pb-2 border-b border-[#eceff1]">
        <div className="text-[18px] font-normal leading-snug">{subject}</div>
        <div className="mt-2 flex items-start gap-2.5">
          <Avatar size={36} />
          <div className="text-[13px] leading-snug">
            <div>
              <span className="font-semibold">{senderName}</span>{' '}
              <span className="text-[#5f6368]">&lt;{senderEmail}&gt;</span>
            </div>
            <div className="text-[#5f6368]">
              {t('approvals.preview.to')} {to}
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 py-3 text-[14px] leading-[1.5] whitespace-pre-wrap break-words">
        {body}
      </div>
    </div>
  );
}

/* A small, safe markdown renderer for Milli's blog drafts: headings,
 * paragraphs, bullet and numbered lists, bold, italics, links. Nothing is
 * injected as HTML. */
function inline(text, keyBase) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`${keyBase}-${i++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('[')) {
      const mm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      parts.push(
        <a
          key={`${keyBase}-${i++}`}
          href={mm[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-[#8F5835]"
        >
          {mm[1]}
        </a>
      );
    } else {
      parts.push(<em key={`${keyBase}-${i++}`}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Markdown({ source }) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let para = [];
  let list = null; // { ordered, items }
  const flushPara = () => {
    if (para.length) {
      const text = para.join(' ');
      out.push(
        <p key={`p${out.length}`} className="my-3">
          {inline(text, `p${out.length}`)}
        </p>
      );
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const Tag = list.ordered ? 'ol' : 'ul';
      out.push(
        <Tag
          key={`l${out.length}`}
          className={`my-3 pl-6 space-y-1 ${list.ordered ? 'list-decimal' : 'list-disc'}`}
        >
          {list.items.map((it, i) => (
            <li key={i}>{inline(it, `l${out.length}-${i}`)}</li>
          ))}
        </Tag>
      );
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const li = /^\s*([-*]|\d+[.)])\s+(.*)$/.exec(line);
    if (!line.trim()) {
      flushPara();
      flushList();
    } else if (h) {
      flushPara();
      flushList();
      const level = h[1].length;
      const cls =
        level <= 2
          ? 'text-[22px] font-semibold mt-6 mb-2 leading-snug'
          : 'text-[17px] font-semibold mt-5 mb-1.5 leading-snug';
      out.push(
        <div key={`h${out.length}`} className={cls} role="heading" aria-level={level}>
          {inline(h[2], `h${out.length}`)}
        </div>
      );
    } else if (li) {
      flushPara();
      const ordered = /\d/.test(li[1]);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(li[2]);
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return <>{out}</>;
}

function ArticleMock({ title, body, ctaUrl, publishDate, business, t }) {
  const when = publishDate
    ? new Date(`${publishDate}T12:00:00`).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
  return (
    <div
      className="rounded-lg bg-[#EDEBE6] text-[#141210] border border-[#d9d5cc] overflow-hidden max-w-[680px]"
      style={{ fontFamily: 'Inter, Helvetica, Arial, sans-serif' }}
      data-testid="preview-article"
    >
      <div className="bg-[#1e2023] px-5 py-3 flex items-center gap-3">
        <img
          src={`${BRAND}/ko_core_lockup_transparent-tight_v1.png`}
          alt={PAGE_NAME}
          className="h-7 w-auto"
        />
        <span className="ml-auto text-[11px] uppercase tracking-[0.16em] text-[#C9A24B]">
          {t('approvals.preview.blog')}
        </span>
      </div>
      <div className="px-5 pt-5 pb-6">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#8F5835]">
          {[business, when].filter(Boolean).join(' · ')}
        </div>
        <h1
          className="mt-2 text-[26px] leading-tight font-bold"
          style={{ fontFamily: 'Cinzel, Georgia, serif' }}
        >
          {title}
        </h1>
        <div className="mt-2 text-[15px] leading-[1.6]">
          <Markdown source={body} />
        </div>
        {ctaUrl ? (
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#C9A24B] text-[#141210] px-4 py-2 text-[13px] font-semibold uppercase tracking-[0.12em]"
          >
            {t('approvals.preview.cta')} <ExternalLink size={14} />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function SocialPreview({ preview, t }) {
  const channels = Array.isArray(preview.channels) && preview.channels.length
    ? preview.channels
    : ['facebook'];
  const [active, setActive] = useState(channels[0]);
  const current = channels.includes(active) ? active : channels[0];
  const labels = {
    facebook: t('approvals.preview.facebook'),
    instagram: t('approvals.preview.instagram'),
  };
  return (
    <div>
      <ChannelTabs channels={channels} active={current} onChange={setActive} labels={labels} />
      {current === 'instagram' ? (
        <InstagramMock caption={preview.caption} imageUrl={preview.imageUrl} t={t} />
      ) : (
        <FacebookMock caption={preview.caption} imageUrl={preview.imageUrl} t={t} />
      )}
    </div>
  );
}

/* A rendered video (076): the MP4 itself with its poster frame, sized by
 * aspect so a Reel stands like a phone and a wide piece lies like a screen.
 * Under it, the scene list as filed, so what was asked for can be checked
 * against what plays. */
function VideoMock({ preview, t }) {
  const portrait = preview.aspect === '9:16';
  const square = preview.aspect === '1:1';
  const maxW = portrait ? 'max-w-[300px]' : square ? 'max-w-[420px]' : 'max-w-[640px]';
  const secs = preview.duration ? `${Math.round(preview.duration)} s` : null;
  return (
    <div data-testid="video-preview">
      <div className={`mx-auto ${maxW} overflow-hidden rounded-lg border border-border bg-black`}>
        <video
          src={preview.videoUrl}
          poster={preview.posterUrl || undefined}
          controls
          playsInline
          preload="metadata"
          className="block w-full"
          style={{ aspectRatio: portrait ? '9 / 16' : square ? '1 / 1' : '16 / 9' }}
        />
      </div>
      <div className="mt-1.5 flex flex-wrap justify-center gap-x-3 text-[11px] text-muted-foreground">
        <span>{preview.aspect}</span>
        {preview.width && preview.height ? <span>{preview.width}×{preview.height}</span> : null}
        {secs ? <span>{secs}</span> : null}
      </div>
      {preview.scenes?.length ? (
        <ol className="mt-3 space-y-1 text-sm" data-testid="video-scenes">
          {preview.scenes.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span className="w-5 shrink-0 text-right text-[11px] text-muted-foreground">{i + 1}</span>
              <span>
                <span className="text-foreground">{s.headline}</span>
                {s.subline ? <span className="text-muted-foreground"> {s.subline}</span> : null}
                <span className="ml-1.5 text-[11px] text-muted-foreground">{t('approvals.preview.sceneMeta', { asset: s.asset, seconds: s.seconds })}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

export default function ApprovalPreview({ preview, t }) {
  if (!preview) return null;
  let body = null;
  if (preview.kind === 'social') body = <SocialPreview preview={preview} t={t} />;
  else if (preview.kind === 'video') body = <VideoMock preview={preview} t={t} />;
  else if (preview.kind === 'email') {
    body = <EmailMock to={preview.to} subject={preview.subject} body={preview.body} business={preview.business} support={preview.support} t={t} />;
  } else if (preview.kind === 'article') {
    body = (
      <ArticleMock
        title={preview.title}
        body={preview.body}
        ctaUrl={preview.ctaUrl}
        publishDate={preview.publishDate}
        business={preview.business}
        t={t}
      />
    );
  } else if (preview.kind === 'content') {
    body = <FacebookMock caption={preview.facebook ?? preview.body} imageUrl={null} t={t} />;
  }
  if (!body) return null;
  return (
    <div data-testid="approval-preview">
      <span className="uppercase tracking-[0.14em] text-[11px] text-muted-foreground">
        {preview.kind === 'video' ? t('approvals.preview.videoTitle') : t('approvals.preview.title')}
      </span>
      <div className="mt-1.5">{body}</div>
    </div>
  );
}
